import '@leanpay/ui/main.css'

import React, { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'
import TagManager from 'react-gtm-module'
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom'

import type { AnalyticsContext } from '@leanpay/analytics'
import { LeanPayAnalyticsProvider } from '@leanpay/analytics'
import { announceStoredGoogleConsent } from '@leanpay/utils'

import App from 'app/app'
import {
    currentEnvironment,
    currentMarket,
    gtmId,
    isProdEnvironment,
    isTestEnvironment,
} from 'library/constants'
import { reloadOnceOnStaleChunk } from 'library/lazy-with-retry'

import './index.css'

import { initI18n } from './i18n'

const sentryClient = Sentry.init({
    dsn: 'https://56d872c3bbdc78970e207b4727b720b2@o4508023659560960.ingest.de.sentry.io/4508023664017488',
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        Sentry.replayIntegration(),
    ],
    enabled: true,
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: currentEnvironment.toLowerCase(),
    ignoreErrors: [
        'Non-Error promise rejection captured with value: VENDOR_API_TOKEN_UNKNOWN',
        'Non-Error promise rejection captured with value: 502',
        'Failed to fetch dynamically imported module',
        'Network Error',
        "Can't find variable: gmo",
        'Fetch request to',
        'The object can not be cloned',
        // Safari phrasings of a stale-chunk load after a redeploy
        'Importing a module script failed',
        'is not a valid JavaScript MIME type',
        // Onfido SDK internals (KYC widget) — not actionable from our code
        'The token has expired, please request a new one',
        /Communication channel with src https:\/\/sdk\.onfido\.com/,
        // In-app browser / WebView bridge noise
        'Java object is gone',
        'SCDynimacBridge',
    ],
})
Sentry.setTag('market', currentMarket.toLowerCase())

if (isProdEnvironment) {
    const tagManagerArgs = {
        gtmId,
    }

    TagManager.initialize(tagManagerArgs)
    announceStoredGoogleConsent()
}

// PostHog Analytics Configuration
const analyticsContext: AnalyticsContext = {
    app: 'checkout',
    market: currentMarket,
    environment: currentEnvironment,
    apiKey: 'phc_aLiRBZwuwIC4HZ5uveu1aJE3aLkpO5jIoWIaXeJ6gGv',
    apiHost: 'https://eu.i.posthog.com',
    disabled: !isTestEnvironment,
    enableHeatmaps: true,
    hostname: window.location.hostname,
}

// Initialize i18n before rendering
initI18n()
    .then(() => {
        ReactDOM.createRoot(document.getElementById('root')!).render(
            <React.StrictMode>
                <LeanPayAnalyticsProvider context={analyticsContext}>
                    <App />
                </LeanPayAnalyticsProvider>
            </React.StrictMode>,
        )
    })
    .catch((error: unknown) => {
        if (reloadOnceOnStaleChunk(error)) return
        Sentry.captureException(error)
    })
